import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { MapPin, type LucideIconData } from 'lucide-angular';

import { IconComponent } from '../../../../shared/ui/icon/icon.component';
import { EVENTS_COPY } from '../../constants/events.constants';
import { EventDialogService } from '../../data/event-dialog.service';
import type { StoreEvent } from '../../data/events.model';
import {
  civilDayKey,
  eventTypeLabel,
  formatEventPrice,
  formatEventRange,
  multiDayStartTime,
} from '../../utils/event-format.util';

const SHORT_MONTHS = [
  'ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN',
  'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC',
] as const;

/**
 * Fila del listado de eventos. Recibe el evento ya mapeado y no sabe de dónde
 * salió, así que sirve igual a `/eventos`, a la portada, a `/comunidad` y a la
 * vista día del diálogo.
 *
 * La tarjeta entera abre el detalle (D21) con un **único tab stop**: un botón en
 * el nombre que se estira sobre la tarjeta. Llama al `EventDialogService`
 * directamente, sin outputs que cablear en cada padre. El enlace externo del
 * evento ya no vive aquí: está en el detalle, como «Más información».
 *
 * Opcionalmente (`showImage`, D29) lleva a la izquierda una miniatura cuadrada
 * del cartel del evento. Es decorativa y no añade tab stops: el botón estirado
 * la cubre. Sin imagen —o si falla al cargar— no deja ni hueco (D31).
 *
 * Todo lo que se pinta con fecha pasa por `event-format.util`, que ancla el
 * formateo a la zona de la tienda.
 */
@Component({
  selector: 'app-event-card',
  imports: [IconComponent],
  templateUrl: './event-card.component.html',
  styleUrl: './event-card.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'contents' },
})
export class EventCardComponent {
  event = input.required<StoreEvent>();
  /**
   * Pinta el chip que identifica el juego del evento. Solo lo activan las
   * listas **globales** (portada y `/comunidad`), donde el usuario no sabe de
   * qué juego es lo que está viendo.
   *
   * Por defecto `false`, que es lo que protege a `/eventos` y al calendario:
   * allí ya hay un filtro por juego encima y el chip sería ruido en cada fila.
   */
  showGame = input(false);
  /**
   * Pinta la miniatura del cartel a la izquierda de la fecha. Solo la activa la
   * página de eventos (`/eventos` y `/juegos/:slug/eventos`), donde la fila
   * tiene sitio para ella.
   *
   * Por defecto `false`, que es lo que protege a la portada, a `/comunidad` y a
   * la vista día del diálogo: sus filas se quedan exactamente como estaban.
   */
  showImage = input(false);

  private readonly eventDialog = inject(EventDialogService);
  private readonly failedImageUrl = signal<string | null>(null);

  protected readonly copy = EVENTS_COPY;
  protected readonly mapPinIcon: LucideIconData = MapPin;

  /** «Ver detalles de {nombre}»: contiene el texto visible, como pide WCAG 2.5.3. */
  protected readonly openLabel = computed(
    () => `${EVENTS_COPY.openDetailsLabel} ${this.event().name}`,
  );

  /**
   * Un evento sin juego —o con uno que no se puede nombrar porque está
   * despublicado (D12)— es un evento de tienda, y se rotula como tal. El chip
   * nunca queda en blanco: con `showGame` activo siempre dice algo.
   */
  protected readonly gameLabel = computed(
    () => this.event().game?.name ?? EVENTS_COPY.storeChip,
  );

  /** Bloque de fecha: día y mes en corto, siempre en hora de Costa Rica. */
  protected readonly dayNumber = computed(() => {
    const key = civilDayKey(this.event().startsAt);
    return key ? String(Number(key.slice(8, 10))) : '—';
  });

  protected readonly monthLabel = computed(() => {
    const key = civilDayKey(this.event().startsAt);
    if (!key) return '';
    return SHORT_MONTHS[Number(key.slice(5, 7)) - 1] ?? '';
  });

  protected readonly typeLabel = computed(() => eventTypeLabel(this.event().type));

  protected readonly dateLabel = computed(() =>
    formatEventRange(this.event().startsAt, this.event().endsAt),
  );

  /**
   * La hora solo se repite aparte en los eventos de varios días: en los de un
   * día `formatEventRange` ya la incluye, y duplicarla sería ruido.
   */
  protected readonly timeLabel = computed(() =>
    multiDayStartTime(this.event().startsAt, this.event().endsAt),
  );

  protected readonly priceLabel = computed(() => formatEventPrice(this.event().price));

  /**
   * URL de la miniatura, o `null` si no toca pintarla (D31): sin `showImage`,
   * sin imagen o con una URL que ya falló. Se guarda la URL fallida y no un
   * booleano para que el fallo no se herede si cambia la imagen del evento.
   */
  protected readonly thumbUrl = computed(() => {
    const url = this.event().imageUrl;
    return this.showImage() && url && url !== this.failedImageUrl() ? url : null;
  });

  protected onThumbError(url: string): void {
    this.failedImageUrl.set(url);
  }

  protected openDetails(): void {
    this.eventDialog.openEvent(this.event());
  }
}
